/**
 * Wochensummen und Überstundensaldo.
 */

import { HOUR_MS, weekStart, type DayKey } from './time.ts';
import { settledNetMs } from './entries.ts';
import type { DayEntry, Employee } from './types.ts';

export interface WeekSummary {
  monday: DayKey;
  netMs: number;
  targetMs: number;
  diffMs: number;
  /** Anteil am Soll, 0..1 (kann über 1 gehen). */
  ratio: number;
}

export function weekSummary(
  employee: Pick<Employee, 'weeklyTargetHours'>,
  monday: DayKey,
  entriesByDate: Map<DayKey, DayEntry>,
  now: number,
): WeekSummary {
  let net = 0;
  for (const [date, entry] of entriesByDate) {
    if (weekStart(date) === monday) net += settledNetMs(entry, now);
  }
  const targetMs = employee.weeklyTargetHours * HOUR_MS;
  return {
    monday,
    netMs: net,
    targetMs,
    diffMs: net - targetMs,
    ratio: targetMs > 0 ? net / targetMs : 0,
  };
}

/**
 * Überstundensaldo: Anfangssaldo plus die Abweichung jeder **abgeschlossenen**
 * Woche vom Wochen-Soll.
 *
 * Drei bewusste Entscheidungen, alle aus dem Entwurf übernommen:
 *
 *  1. Die laufende Woche zählt nicht mit. Sonst stünde man montags früh
 *     automatisch mit einem Minus von fast einer ganzen Woche da.
 *  2. Wochen ohne jede Buchung werden übersprungen, statt das volle Soll
 *     anzulasten. Urlaub und Krankheit sind noch nicht erfasst; ein Minus
 *     dafür wäre schlicht falsch.
 *  3. Beginnt das Arbeitsverhältnis mitten in der Woche, fällt diese erste
 *     Teilwoche heraus — ein volles Wochensoll gegen drei Arbeitstage zu
 *     rechnen erzeugt ein Defizit, das es nie gab.
 */
export function overtimeBalanceMs(
  employee: Pick<Employee, 'weeklyTargetHours' | 'openingBalanceHours' | 'employmentStart'>,
  entries: DayEntry[],
  now: number,
  today: DayKey,
): number {
  const currentMonday = weekStart(today);
  const perWeek = new Map<DayKey, number>();

  for (const entry of entries) {
    const monday = weekStart(entry.date);
    if (monday >= currentMonday) continue; // (1)
    perWeek.set(monday, (perWeek.get(monday) ?? 0) + settledNetMs(entry, now));
  }

  // (3) — Anker ist der Eintrittstag, ersatzweise die erste Buchung.
  const anchor =
    employee.employmentStart ||
    entries.map((e) => e.date).sort()[0];
  if (anchor && weekStart(anchor) !== anchor) {
    perWeek.delete(weekStart(anchor));
  }

  const targetMs = employee.weeklyTargetHours * HOUR_MS;
  let balance = employee.openingBalanceHours * HOUR_MS;
  for (const net of perWeek.values()) balance += net - targetMs; // (2)
  return balance;
}
